// src/components/ModuleSelectionModal.tsx
import React, { useMemo, useState } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Title,
  NavLink,
  Divider,
  Box,
  Badge,
  Button,
  ScrollArea,
  Tooltip,
} from "@mantine/core";
import { FiLock } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { DisciplineBadge } from "../../components/DisciplinBadge";
import { RoleChip } from "../../components/RoleChip";
import { changeActiveModuleThunk } from "../designThunk";
import { IMultiplacerModuleKey, MEDIA_ASSET_KEYS } from "@multiconsult-group/multiplacer-core";
import {
  selectAvailableModulesForCurrentUserInCurrentProject,
  selectIsCurrentUserAccessReady,
  selectVisibleModulesForCurrentProject,
} from "../../projectSelectors";
import { MediaAssetViewer } from "../../MediaAssets/MediaAssetViewer";
import { MODULE_ICON_BY_KEY } from "../designModuleIcons";
import { useInternalMsalAccount } from "../../hooks/useInternalMsalAccount";

type ModuleSelectionModalProps = {
  onClose?: () => void;
};

export const ModuleSelectionModal: React.FC<ModuleSelectionModalProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const activeModule = useAppSelector((s) => s.design.activeModule);

  const availableModuleKeys = useAppSelector(selectAvailableModulesForCurrentUserInCurrentProject);
  const userAccessReady = useAppSelector(selectIsCurrentUserAccessReady);
  const isInternalMsalAccount = useInternalMsalAccount();
  const accessReady = userAccessReady || isInternalMsalAccount;
  const visibleModules = useAppSelector(selectVisibleModulesForCurrentProject);

  const hasAccessTo = (key: IMultiplacerModuleKey) => {
    if (!accessReady) return false;
    if (isInternalMsalAccount) return true;
    return (availableModuleKeys as readonly IMultiplacerModuleKey[]).includes(key);
  };

  const initialSelection: IMultiplacerModuleKey | null = useMemo(() => {
    if (activeModule && visibleModules.some((module) => module.key === activeModule)) {
      return activeModule;
    }
    return visibleModules[0]?.key ?? null;
  }, [activeModule, visibleModules]);

  const [selectedKey, setSelectedKey] = useState<IMultiplacerModuleKey | null>(initialSelection);

  const selectedModule = useMemo(() => {
    return visibleModules.find((m) => m.key === selectedKey) ?? null;
  }, [selectedKey, visibleModules]);

  const selectedHasAccess = selectedKey ? hasAccessTo(selectedKey) : false;
  const SelectedIcon = selectedModule ? MODULE_ICON_BY_KEY[selectedModule.key] : null;

  const handleConfirm = () => {
    if (!accessReady || !selectedKey) return;
    if (!hasAccessTo(selectedKey)) return;

    dispatch(changeActiveModuleThunk(selectedKey));
    onClose?.();
  };
  const assetKey = useMemo(() => {
    switch (selectedKey) {
      case "CEILING":
        return MEDIA_ASSET_KEYS.CEILING_MODULE;
      case "RFP":
        return MEDIA_ASSET_KEYS.RFP_MODULE;
      case "EXPLORE":
        return MEDIA_ASSET_KEYS.EXPLORE_MODULE;
    }
  }, [selectedKey]);

  return (
    <Modal
      opened
      onClose={() => onClose?.()}
      radius="md"
      size={900}
      centered
      overlayProps={{ opacity: 0.2, blur: 6 }}
      title={
        <div>
          <Title order={3}>Velg modul</Title>
          <Text size="sm" c="dimmed">
            Multiplacer er delt inn i moduler. Velg hvilken modul du vil jobbe i nå.
          </Text>
        </div>
      }
    >
      <Box style={{ display: "flex", flexDirection: "column", height: "50vh" }}>
        <Box style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {/* Left: Module list */}
          <Box
            style={{
              flex: "0 0 280px",
              borderRight: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            <ScrollArea h="100%" type="hover">
              <Box style={{ padding: 10 }}>
                <Stack gap={6}>
                  {visibleModules.map((mod) => {
                    const isSelected = mod.key === selectedKey;
                    const Icon = MODULE_ICON_BY_KEY[mod.key];

                    const canUse = hasAccessTo(mod.key);

                    const link = (
                      <NavLink
                        key={mod.key}
                        active={isSelected}
                        label={
                          <Group gap="xs" wrap="nowrap" justify="space-between">
                            <Text truncate fw={isSelected ? 600 : 400}>
                              {mod.title}
                            </Text>

                            {!accessReady ? (
                              <Badge size="xs" variant="light" color="gray">
                                Laster
                              </Badge>
                            ) : canUse ? (
                              <Badge size="xs" variant="light">
                                Tilgang
                              </Badge>
                            ) : (
                              <Badge size="xs" variant="light" color="gray">
                                Ingen tilgang
                              </Badge>
                            )}
                          </Group>
                        }
                        leftSection={
                          <Group gap={8} align="center" wrap="nowrap">
                            <Icon size={16} />
                            {accessReady && !canUse ? (
                              <FiLock size={14} color="var(--mantine-color-gray-6)" />
                            ) : null}
                          </Group>
                        }
                        onClick={() => setSelectedKey(mod.key)} // <-- alltid klikkbar
                        variant="subtle"
                        styles={{
                          root: { borderRadius: 8, padding: "12px 12px" },
                          label: { width: "100%" },
                        }}
                      />
                    );

                    return canUse ? (
                      <React.Fragment key={mod.key}>{link}</React.Fragment>
                    ) : (
                      <Tooltip
                        key={mod.key}
                        withArrow
                        position="right"
                        maw={320}
                        multiline
                        label="Du har ikke tilgang til å aktivere denne modulen i dette prosjektet."
                      >
                        <Box>{link}</Box>
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            </ScrollArea>
          </Box>

          {/* Right: Details */}
          <Box
            style={{
              flex: 1,
              paddingTop: 18,
              paddingRight: 18,
              paddingBottom: 18,
              paddingLeft: 26,
              minHeight: 0,
            }}
          >
            <ScrollArea h="100%" type="hover">
              {!selectedModule ? (
                <Text size="sm" c="dimmed" ta="center" mt="xl">
                  Velg en modul til venstre
                </Text>
              ) : (
                <Stack gap="lg" pr="xs">
                  <Group gap="sm" align="center" justify="space-between">
                    <Group gap="sm" align="center">
                      {SelectedIcon ? <SelectedIcon size={24} /> : null}
                      <Title order={4}>{selectedModule.title}</Title>
                    </Group>

                    {selectedKey ? (
                      !accessReady ? (
                        <Badge variant="light" color="gray">
                          Laster
                        </Badge>
                      ) : selectedHasAccess ? (
                        <Badge variant="light">Tilgang</Badge>
                      ) : (
                        <Badge variant="light" color="gray" leftSection={<FiLock size={14} />}>
                          Ingen tilgang
                        </Badge>
                      )
                    ) : null}
                  </Group>

                  <Text size="sm">{selectedModule.description}</Text>

                  {accessReady && !selectedHasAccess ? (
                    <Text size="sm" c="dimmed">
                      Du kan se informasjon om modulen, men du har ikke tilgang til å aktivere den i
                      dette prosjektet.
                    </Text>
                  ) : null}

                  <Divider />

                  <Stack gap={8}>
                    <Text size="xs" fw={500} c="dimmed">
                      Disipliner
                    </Text>
                    <Group gap={8}>
                      {selectedModule.disciplines.map((d) => (
                        <DisciplineBadge key={d} discipline={d} />
                      ))}
                    </Group>
                  </Stack>

                  <Stack gap={8}>
                    <Text size="xs" fw={500} c="dimmed">
                      Typiske roller
                    </Text>
                    <Group gap={8}>
                      {selectedModule.roles.map((r) => (
                        <RoleChip key={r} role={r} />
                      ))}
                    </Group>
                  </Stack>

                  <Stack gap={8}>
                    <Text size="xs" fw={500} c="dimmed">
                      Faser
                    </Text>
                    <Group gap={8}>
                      {selectedModule.phases.map((phase) => (
                        <Badge key={phase} variant="outline" size="sm">
                          {phase}
                        </Badge>
                      ))}
                    </Group>
                  </Stack>
                  {assetKey != null && (
                    <MediaAssetViewer assetKey={MEDIA_ASSET_KEYS[assetKey]} mode="EXPANDED" />
                  )}
                </Stack>
              )}
            </ScrollArea>
          </Box>
        </Box>

        <Divider my="md" />

        <Group justify="flex-end">
          <Button
            onClick={handleConfirm}
            disabled={!accessReady || !selectedKey || !selectedHasAccess}
          >
            Aktiver modul
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
